import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AuthService from 'services/auth';
import ApiService from 'services/api';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import { PillButton } from 'components/atoms';
import './CliAuthorizePage.css';

// Defensive decoder for display names. Some user records in DDB have
// names stored URL-encoded ("Jianbo%20Cui") because of a historical
// double-encode bug in the OAuth redirect path. Decode once on display
// so the UI is correct regardless of what shape ended up in localStorage.
const safeDecodeName = (s) => {
  if (!s || typeof s !== 'string') return s;
  if (!s.includes('%')) return s;
  try { return decodeURIComponent(s); } catch { return s; }
};

const CliAuthorizePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending'); // pending, success, denied, error
  const [error, setError] = useState(null);
  
  const deviceCode = searchParams.get('deviceCode');
  const user = AuthService.getCurrentUser();

  // CLI auth lives on STUDIO subdomain (the app users actually log
  // into). If a stale CLI version (or a manual visit) lands on the
  // root marketing domain, bounce immediately to studio with the
  // same deviceCode preserved. Cookies on root vs studio don't share
  // session reliably, so root would otherwise read whatever stale
  // cookie is sitting at .zibby.dev / zibby.dev scope and authorize
  // the WRONG account (real bug 2026-05-09).
  useEffect(() => {
    const host = window.location.hostname;
    const onRootZibby =
      host === 'zibby.dev' || host === 'zibby.app' ||
      host === 'www.zibby.dev' || host === 'www.zibby.app';
    if (onRootZibby) {
      const studioHost = host.startsWith('www.') ? host.replace('www.', 'studio.') : `studio.${host}`;
      const target = `https://${studioHost}/authorize${window.location.search}`;
      window.location.replace(target);
      return;
    }
    if (!AuthService.isAuthenticated()) {
      console.error('User not authenticated, redirecting to login');
      navigate(`/connect${window.location.search}`);
    }
  }, [navigate]);

  const handleAuthorize = async (approve) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Authorizing device:', deviceCode, 'approve:', approve);
      console.log('Auth check:', AuthService.isAuthenticated());
      console.log('Session token:', AuthService.getSessionToken()?.substring(0, 20) + '...');
      console.log('User:', AuthService.getCurrentUser());
      
      // Use ApiService which handles auth automatically
      const response = await ApiService.post('/cli/login/authorize', {
        deviceCode,
        approve,
      });

      console.log('Authorization response:', response);

      // ApiService.post returns the parsed JSON data directly
      if (approve) {
        setStatus('success');
        setTimeout(() => {
          navigate('/projects');
        }, 3000);
      } else {
        setStatus('denied');
        setTimeout(() => {
          navigate('/projects');
        }, 2000);
      }
    } catch (err) {
      console.error('Authorization error:', err);
      setError(err.message || 'Failed to process authorization');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (!deviceCode) {
    return (
      <>
        <Helmet>
          <title>Invalid Request - Zibby</title>
        </Helmet>
        <div className="cli-authorize-wrapper">
          <div className="cli-authorize-page">
            <PublicNav transparent />
            <div className="cli-authorize-container">
            <div className="cli-authorize-card">
              <h2>Invalid Request</h2>
                <p className="cli-authorize-info">No device code provided</p>
                <PillButton type="button" onClick={() => navigate('/projects')}>
                  <span className="button-text">Go to Dashboard</span>
                </PillButton>
              </div>
            </div>
          </div>
          <PublicFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Authorize CLI - Zibby</title>
      </Helmet>
      <div className="cli-authorize-wrapper">
        <div className="cli-authorize-page">
          <PublicNav transparent />
          <div className="cli-authorize-container">
            <div className="cli-authorize-card">
              {status === 'pending' && (
                <>
                  <h1 className="cli-authorize-title">Authorize CLI Access</h1>
                  <p className="cli-authorize-info">
                    A CLI application is requesting access to your Zibby account.
                  </p>
                  {/* Account display — keep brand styling (.cli-user-info)
                      but include both name AND email so users can spot a
                      mismatched session before clicking Authorize. The
                      historical bug: cross-domain / leftover cookies made
                      it possible to authorize a device for the WRONG
                      account if the user thought they had switched. */}
                  <div className="cli-user-info">
                    <div style={{ fontSize: 11, opacity: 0.65, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 400 }}>
                      You are about to authorize as
                    </div>
                    <div>{safeDecodeName(user?.name) || user?.email}</div>
                    {user?.email && safeDecodeName(user?.name) && (
                      <div style={{ fontSize: 13, opacity: 0.65, marginTop: 4, fontWeight: 400 }}>
                        {user.email}
                      </div>
                    )}
                  </div>
                  <p className="cli-authorize-description">
                    This will allow the CLI to upload test results and access your projects.
                  </p>
                  <div className="cli-authorize-buttons">
                    <PillButton
                      type="button"
                      onClick={() => handleAuthorize(true)}
                      disabled={loading}
                    >
                      {loading && <div className="spinner"></div>}
                      <span className="button-text">{loading ? 'Authorizing...' : 'Authorize'}</span>
                    </PillButton>
                    <button
                      type="button"
                      className="cli-deny-button"
                      onClick={() => handleAuthorize(false)}
                      disabled={loading}
                    >
                      Deny
                    </button>
                  </div>
                  <div style={{ marginTop: 24, fontSize: 13, textAlign: 'center', opacity: 0.75 }}>
                    Not you?{' '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        // Preserve deviceCode through the round-trip so
                        // after re-login the user lands back on this
                        // authorize page with the same code they were
                        // originally trying to approve. ConnectPage
                        // already reads ?deviceCode= and bounces to
                        // /cli/authorize after a successful login.
                        const returnTo = `/connect?deviceCode=${encodeURIComponent(deviceCode)}`;
                        AuthService.logout(returnTo);
                      }}
                      style={{ color: '#6366f1', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Sign in as a different user
                    </a>
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <h1 className="cli-authorize-title">CLI Authorized!</h1>
                  <p className="cli-authorize-success">
                    You can now return to your terminal and continue.
                  </p>
                  <p className="cli-redirect-info">Redirecting to dashboard...</p>
                </>
              )}

              {status === 'denied' && (
                <>
                  <h1 className="cli-authorize-title">Authorization Denied</h1>
                  <p className="cli-authorize-info">You have denied CLI access.</p>
                  <p className="cli-redirect-info">Redirecting to dashboard...</p>
                </>
              )}

              {status === 'error' && (
                <>
                  <h1 className="cli-authorize-title">Authorization Failed</h1>
                  <p className="cli-error-message">{error}</p>
                  <PillButton type="button" onClick={() => navigate('/projects')}>
                    <span className="button-text">Go to Dashboard</span>
                  </PillButton>
                </>
              )}
            </div>
          </div>
        </div>
        <PublicFooter />
      </div>
    </>
  );
};

export default CliAuthorizePage;
